import React, { useEffect } from 'react';
import {
  Modal,
  Spin,
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Typography,
  Space,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useUserCreditSummary } from '../hooks/useUserCreditSummary';
import { CreditTransaction, CreditTransactionType } from '../../api/services/adminService';

const { Text } = Typography;

const TYPE_COLORS: Record<CreditTransactionType, string> = {
  PURCHASE: 'blue',
  SUBSCRIPTION_GRANT: 'green',
  AD_POST: 'orange',
  ADMIN_ADJUSTMENT: 'purple',
  REFUND: 'red',
};

interface Props {
  userId: string | null;
  visible: boolean;
  onClose: () => void;
}

const UserCreditSummaryModal: React.FC<Props> = ({ userId, visible, onClose }) => {
  const { t } = useTranslation();
  const { summary, loading, fetchSummary, clear } = useUserCreditSummary();

  useEffect(() => {
    if (visible && userId) {
      fetchSummary(userId);
    } else {
      clear();
    }
  }, [visible, userId]);

  const columns: ColumnsType<CreditTransaction> = [
    {
      title: t('admin.user_credit_modal.col_date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: t('admin.user_credit_modal.col_type'),
      dataIndex: 'type',
      key: 'type',
      width: 160,
      render: (type: CreditTransactionType) => (
        <Tag color={TYPE_COLORS[type]}>{type.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: t('admin.user_credit_modal.col_amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 90,
      render: (amount: number) => (
        <Text strong style={{ color: amount > 0 ? '#52c41a' : '#ff4d4f' }}>
          {amount > 0 ? `+${amount}` : amount}
        </Text>
      ),
    },
    {
      title: t('admin.user_credit_modal.col_balance_after'),
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      width: 110,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: t('admin.user_credit_modal.col_listing'),
      key: 'listing',
      render: (_: unknown, record: CreditTransaction) => {
        if (record.listing) {
          const { make, model, year } = record.listing;
          return (
            <Text style={{ fontSize: 12 }}>
              {[year, make, model].filter(Boolean).join(' ') || record.referenceId}
            </Text>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: t('admin.user_credit_modal.col_reason'),
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
  ];

  return (
    <Modal
      title={
        summary
          ? t('admin.user_credit_modal.title_with', { name: `${summary.user.firstName} ${summary.user.lastName}` })
          : t('admin.user_credit_modal.title')
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={960}
      styles={{ body: { padding: '16px 24px' } }}
    >
      <Spin spinning={loading}>
        {summary && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* User summary stats */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false} className="admin-stat-card" style={{ background: '#f0f9ff' }}>
                  <Statistic
                    title={t('admin.user_credit_modal.stat_current_balance')}
                    value={summary.user.currentBalance}
                    prefix={<WalletOutlined />}
                    valueStyle={{ color: '#fe5a1d' }}
                    suffix={t('admin.user_credit_modal.suffix_credits')}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false} className="admin-stat-card" style={{ background: '#f6ffed' }}>
                  <Statistic
                    title={t('admin.user_credit_modal.stat_total_received')}
                    value={summary.summary.totalCreditsReceived}
                    prefix={<ArrowUpOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                    suffix={t('admin.user_credit_modal.suffix_credits')}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false} className="admin-stat-card" style={{ background: '#fff1f0' }}>
                  <Statistic
                    title={t('admin.user_credit_modal.stat_total_used')}
                    value={summary.summary.totalCreditsUsed}
                    prefix={<ArrowDownOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                    suffix={t('admin.user_credit_modal.suffix_credits')}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false} className="admin-stat-card">
                  <Statistic
                    title={t('admin.user_credit_modal.stat_total_txns')}
                    value={summary.summary.totalTransactions}
                  />
                </Card>
              </Col>
            </Row>

            {/* Transaction history */}
            <Table<CreditTransaction>
              rowKey="id"
              dataSource={summary.transactions}
              columns={columns}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              size="small"
              scroll={{ x: 700 }}
              className="admin-table"
            />
          </Space>
        )}
      </Spin>
    </Modal>
  );
};

export default UserCreditSummaryModal;
